import { supabase } from "@/integrations/supabase/client";
import { networkService } from "@/services/network.service";
import { offlineDb } from "@/db/offlineDb";
import OfflineLogger from "@/utils/offlineLogger";

const DAYS_AGO = 30;
const LIMIT_COUNT = 6;

export interface MostPurchasedProduct {
  product_id: string;
  product_name: string;
  count: number;
  category_id?: string;
  category_name?: string;
  category_color?: string;
  category_text_color?: string;
}

interface MostPurchasedRaw {
  product_id: string;
  count: number;
}

/**
 * Legge i prodotti più acquistati dalla cache offline (Dexie), escludendo i
 * prodotti già eliminati localmente e arricchendoli con il colore categoria.
 *
 * Centralizza il blocco che in precedenza era triplicato nel componente
 * (fallback RPC vuota, percorso offline, fallback su errore).
 */
const loadFromOfflineCache = async (): Promise<MostPurchasedProduct[]> => {
  const cachedData = await offlineDb.mostPurchasedCache
    .orderBy("count")
    .reverse()
    .limit(LIMIT_COUNT)
    .toArray();

  const productIds = cachedData.map((item) => item.product_id);
  const products = await offlineDb.products.bulkGet(productIds);
  const validIds = new Set(
    products.filter((p) => p && p._syncStatus !== "deleted").map((p) => p!.id),
  );
  const filteredCached = cachedData.filter((item) => validIds.has(item.product_id));

  const categories = await offlineDb.categories.toArray();

  return filteredCached.map((item) => {
    const cat = categories.find((c) => c.id === item.category_id);
    return {
      product_id: item.product_id,
      product_name: item.product_name,
      count: item.count,
      category_id: item.category_id,
      category_name: item.category_name,
      category_color: cat?.color,
      category_text_color: cat?.text_color,
    } as MostPurchasedProduct;
  });
};

/**
 * Recupera i prodotti più acquistati negli ultimi 30 giorni.
 *
 * Online: usa la RPC Supabase `get_most_purchased_products`, arricchisce con
 * nome prodotto e categoria, e aggiorna la cache offline. Se la RPC non
 * restituisce dati, ricade sulla cache locale. Offline (o su errore di rete):
 * legge direttamente dalla cache.
 */
export const getMostPurchasedProducts = async (): Promise<MostPurchasedProduct[]> => {
  if (!networkService.isOnline()) {
    return loadFromOfflineCache();
  }

  try {
    return await fetchOnline();
  } catch (error) {
    // Errore di rete/RPC online: ricade sulla cache locale come l'originale.
    OfflineLogger.error("getMostPurchasedProducts.online", error);
    return loadFromOfflineCache();
  }
};

const fetchOnline = async (): Promise<MostPurchasedProduct[]> => {
  const { data, error } = await supabase.rpc("get_most_purchased_products", {
    days_ago: DAYS_AGO,
    limit_count: LIMIT_COUNT,
  });

  if (error) throw error;

  const typedData = data as MostPurchasedRaw[] | null;

  if (!typedData || typedData.length === 0) {
    // Nessun dato online: ricade sulla cache offline.
    return loadFromOfflineCache();
  }

  const productIds = typedData.map((item) => item.product_id);
  const { data: productsData } = await supabase
    .from("user_products")
    .select("id, name, category_id")
    .in("id", productIds);

  // Filtra i prodotti che esistono ancora (non eliminati)
  const existingIds = new Set(productsData?.map((p) => p.id));
  const filteredData = typedData.filter((item) => existingIds.has(item.product_id));

  const categoryIds =
    productsData
      ?.filter((p) => existingIds.has(p.id) && p.category_id)
      .map((p) => p.category_id as string) || [];
  const { data: categoriesData } = await supabase
    .from("user_categories")
    .select("id, name, color, text_color")
    .in("id", categoryIds);

  const enrichedProducts = filteredData.map((item) => {
    const product = productsData?.find((p) => p.id === item.product_id);
    const category = categoriesData?.find((c) => c.id === product?.category_id);

    return {
      product_id: item.product_id,
      product_name: product?.name || "Prodotto sconosciuto",
      count: item.count,
      category_id: category?.id,
      category_name: category?.name,
      category_color: category?.color,
      category_text_color: category?.text_color,
    } as MostPurchasedProduct;
  });

  // Aggiorna la cache offline per uso futuro (best-effort, non bloccante).
  await Promise.all(
    enrichedProducts.map(async (product) => {
      try {
        await offlineDb.mostPurchasedCache.put({
          product_id: product.product_id,
          product_name: product.product_name,
          count: product.count,
          category_id: product.category_id,
          category_name: product.category_name,
          cached_at: Date.now(),
        });
      } catch (err) {
        OfflineLogger.error("getMostPurchasedProducts.cache", err);
      }
    }),
  );

  return enrichedProducts;
};
